import { bool, func } from 'prop-types';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BenefitsContext, FormCheckbox, FormInput, Loader, Modal } from 'components';
import { useFetch, useUser } from 'hooks';

const FiscalModal = ({ isModalOpen, setIsModalOpen }) => {
  const { currentUser } = useUser();
  const { call: addTaxesCall, loading } = useFetch();
  const { taxeMinusTwoYear, getTaxes, taxesLoading } = useContext(BenefitsContext);
  const { t } = useTranslation();

  const fiscalForm = useForm({
    mode: 'all',
    defaultValues: {
      fq: taxeMinusTwoYear?.fq,
      nbParts: taxeMinusTwoYear?.nbParts,
      income: taxeMinusTwoYear?.income,
      department: taxeMinusTwoYear?.department,
      singleParent: taxeMinusTwoYear?.singleParent,
      handicap: taxeMinusTwoYear?.handicap
    }
  });

  const onSubmit = async (newUserData) => {
    const { data } =
      (await addTaxesCall({
        service: 'castelis',
        method: 'post',
        path: `/users/taxeData/${currentUser.serialNumber}`,
        query: newUserData
      })) || {};

    if (data?.status === 200) {
      getTaxes();
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    setFieldData();
  }, [taxeMinusTwoYear]);

  const setFieldData = () => {
    fiscalForm.setValue('fq', taxeMinusTwoYear?.fq ?? 0);
    fiscalForm.setValue('nbParts', taxeMinusTwoYear?.nbParts ?? 0);
    fiscalForm.setValue('income', taxeMinusTwoYear?.income ?? 0);
    fiscalForm.setValue('department', taxeMinusTwoYear?.department ?? '');
    fiscalForm.setValue('singleParent', taxeMinusTwoYear?.singleParent ?? false);
    fiscalForm.setValue('handicap', taxeMinusTwoYear?.handicap ?? false);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      size="medium"
      header={<h2 className="h2 underlined-yellow">{t('dashboard:benefits.updateFiscalData')}</h2>}
      body={
        <FormProvider {...fiscalForm}>
          <form onSubmit={fiscalForm.handleSubmit(onSubmit)} className="fiscal-form">
            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className="label col-span-4"> {t('form:referenceTaxIncome')}</p>
              <FormInput
                name="income"
                type="number"
                placeholder={t('form:referenceTaxIncome')}
                className="col-span-8"
                rules={{ required: true }}
              />
            </div>
            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className="label col-span-4"> {t('form:overseasDepartment')}</p>
              <FormInput name="department" placeholder={t('form:overseasDepartment')} className="col-span-8" />
            </div>

            <FormCheckbox name="handicap" labelI18nKey="form:beneficiary" className="mb-4" />

            <FormCheckbox name="singleParent" labelI18nKey="form:monoparentalFamily" className="mb-4" />

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className="label col-span-4"> {t('form:taxNumber')}</p>
              <FormInput
                name="nbParts"
                placeholder={t('form:taxNumber')}
                className="col-span-8"
                rules={{ required: true }}
              />
            </div>
            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className="label col-span-4">{t('form:familyQuotient')}</p>
              <FormInput
                name="fq"
                placeholder={t('form:familyQuotient')}
                className="col-span-8"
                rules={{ required: true }}
              />
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={!fiscalForm.formState.isValid || loading} className="btn">
                {taxesLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                {t('buttons.validate')}
              </button>
            </div>
          </form>
        </FormProvider>
      }
      onClose={() => setIsModalOpen(false)}
    />
  );
};

FiscalModal.propTypes = {
  isModalOpen: bool.isRequired,
  setIsModalOpen: func.isRequired
};

export default FiscalModal;
